import { countWords, inlineLinks } from "../domain/richText";
import {
  ARTICLES_PATH,
  READING_WORDS_PER_MINUTE,
  SITE_URL,
  type Article,
  type ArticleBlock,
} from "../shared";
import { ARTICLES } from "./articles.data";

/**
 * The reading surface, read-only.
 *
 * Every discovery surface goes through `listIndexable` rather than the raw
 * array, so a `noindex` article cannot reach the index page, the sitemap, or a
 * related-reading list. It still has a live URL: hiding a page from the
 * catalogue and deleting it are different decisions.
 */

/** Newest revision first, which is the order the index reads best in. */
function byRecency(left: Article, right: Article): number {
  if (left.updated !== right.updated) return left.updated < right.updated ? 1 : -1;
  return left.slug < right.slug ? -1 : 1;
}

export function listArticles(): Article[] {
  return [...ARTICLES].sort(byRecency);
}

export function listIndexableArticles(): Article[] {
  return listArticles().filter((article) => !article.noindex);
}

export function getArticleBySlug(slug: string): Article | undefined {
  return ARTICLES.find((article) => article.slug === slug);
}

export function articlePath(slug: string): string {
  return `${ARTICLES_PATH}/${slug}`;
}

/** Canonical URLs and JSON-LD ids are absolute, and this is the only builder. */
export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path}`;
}

/** The authored text of a block, markers and all. */
export function blockText(block: ArticleBlock): string {
  switch (block.kind) {
    case "paragraph":
    case "heading":
    case "subheading":
      return block.text;
    case "list":
      return block.items.join(" ");
    case "table":
      return [block.caption ?? "", ...block.columns, ...block.rows.flat()].join(" ");
    case "callout":
      return `${block.title} ${block.text}`;
  }
}

/**
 * Everything a reader reads, in order. The FAQ answers count: they sit on the
 * page, so leaving them out would under-report the time on the longest pages.
 */
export function articleText(article: Article): string {
  return [
    article.title,
    article.lede,
    ...article.body.map(blockText),
    ...article.faqs.flatMap((faq) => [faq.question, faq.answer]),
  ].join("\n");
}

/**
 * Computed once and used twice: the minutes printed under the title and the
 * `timeRequired` in the article's structured data are the same number by
 * construction, never by two people agreeing.
 */
export function readingTimeMinutes(article: Article): number {
  return Math.max(1, Math.round(countWords(articleText(article)) / READING_WORDS_PER_MINUTE));
}

/** Every local path the article's prose points at, deduplicated. */
export function articleInternalLinks(article: Article): string[] {
  const links = [
    article.ctaHref,
    ...article.body.flatMap((block) => inlineLinks(blockText(block))),
  ].filter((href) => href.startsWith("/"));

  return [...new Set(links)];
}

/**
 * Where an article hands the reader on. Explicit `related` slugs come first,
 * then anything sharing a tag, so an editorial choice is never overruled by the
 * tag graph and a thin article still ends up with somewhere to go.
 */
export function relatedArticles(article: Article, limit = 3): Article[] {
  const indexable = listIndexableArticles().filter(
    (candidate) => candidate.slug !== article.slug,
  );
  const chosen: Article[] = [];

  for (const slug of article.related) {
    const match = indexable.find((candidate) => candidate.slug === slug);
    if (match && !chosen.includes(match)) chosen.push(match);
  }

  const tags = new Set(article.tags);
  for (const candidate of indexable) {
    if (chosen.length >= limit) break;
    if (chosen.includes(candidate)) continue;
    if (candidate.tags.some((tag) => tags.has(tag))) chosen.push(candidate);
  }

  return chosen.slice(0, limit);
}

/** The categories the index offers, in the order their articles appear. */
export function listCategories(): string[] {
  return [...new Set(listIndexableArticles().map((article) => article.category))];
}
